import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import uPlot from "uplot";

/** Charts joining this group share a cursor and zoom together on the x axis. */
const SYNC_KEY = "lmustats-traces";

const AXIS_STROKE = "#a1a1aa";
const GRID_STROKE = "#27272a";
/** Fixed so every chart in a stack lines up down the left edge. */
const Y_AXIS_SIZE = 56;
/** Room for the tick labels under the bottom chart; a hairline under the others. */
const X_AXIS_SIZE = 34;
const X_AXIS_SIZE_HIDDEN = 6;
/** Keeps two cursor labels from printing on top of each other. */
const LABEL_SPACING_PX = 15;

/** Holds each value until the next one, rather than sloping between them. */
const STEPPED_PATH = uPlot.paths.stepped?.({ align: 1 });

export interface ChartSeries {
  label: string;
  colour: string;
  values: Array<number | null>;
}

export interface ChartSpec {
  key: string;
  label: string;
  unit: string | null;
  series: ChartSeries[];
  /** Pins the y range, which keeps percentages readable at 0..100. */
  range?: [number, number];
  /** Draws a dashed reference line at zero, for delta traces. */
  zeroLine?: boolean;
  height?: number;
  /**
   * For channels that hold their value between samples, such as gear: draws a
   * staircase and labels only whole numbers on the y axis.
   */
  stepped?: boolean;
  /** Prints a value for the cursor labels. */
  format?: (value: number) => string;
}

export interface Range {
  min: number;
  max: number;
}

/** A stretch of track worth looking at, shaded behind every chart in the stack. */
export interface Zone {
  key: string;
  fromM: number;
  toM: number;
  /** Drawn at the top of the band on the first chart only. */
  badge: string;
  /** Lifts the one the pointer is on out of the others. */
  active?: boolean;
}

interface Props {
  /** Shared x axis in metres. */
  distance: Float64Array;
  /** Must be referentially stable across renders, or every chart is rebuilt. */
  charts: ChartSpec[];
  /** Zoom window in metres, or null for the full lap. */
  range: Range | null;
  onRangeChange: (range: Range | null) => void;
  onHoverIndex: (index: number | null) => void;
  /** Shaded stretches, e.g. the corners the coach picked out. */
  zones?: Zone[];
}

/**
 * A stack of charts over one shared distance axis.
 *
 * Reading telemetry means correlating channels: a lift shows up in speed, throttle and
 * steering at the same point on the track, and a single chart hides that. The cursor
 * and the zoom are shared across the stack, the hovered index is reported upward so the
 * track map can show where the car is, and the zoom is reported too so that dragging out
 * a section of chart and picking a corner from the list are the same gesture.
 */
export function TraceCharts({
  distance,
  charts,
  range,
  onRangeChange,
  onHoverIndex,
  zones,
}: Props) {
  return (
    <div className="divide-y divide-zinc-800">
      {charts.map((spec, index) => (
        <SyncedChart
          key={spec.key}
          distance={distance}
          spec={spec}
          showXAxis={index === charts.length - 1}
          range={range}
          // One chart reports for the group: they all hold the same scale, and having
          // each of them push the same update back is just noise.
          onRangeChange={index === 0 ? onRangeChange : undefined}
          onHoverIndex={onHoverIndex}
          zones={zones}
          // Numbered once at the top of the stack; on every chart it would be five
          // copies of the same label down the page.
          badgeZones={index === 0}
        />
      ))}
    </div>
  );
}

interface SyncedChartProps {
  distance: Float64Array;
  spec: ChartSpec;
  showXAxis: boolean;
  range: Range | null;
  onRangeChange?: (range: Range | null) => void;
  onHoverIndex: (index: number | null) => void;
  zones?: Zone[];
  badgeZones: boolean;
}

function SyncedChart({
  distance,
  spec,
  showXAxis,
  range,
  onRangeChange,
  onHoverIndex,
  zones,
  badgeZones,
}: SyncedChartProps) {
  const { t } = useTranslation();
  const axisLabel = t("workspace.distanceAxis");
  const containerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);

  // Held in refs so a new callback identity on each parent render, which happens on
  // every mouse move, does not tear down and rebuild the plot.
  const onHoverRef = useRef(onHoverIndex);
  const onRangeChangeRef = useRef(onRangeChange);
  const rangeRef = useRef(range);
  const zonesRef = useRef(zones);
  useEffect(() => {
    onHoverRef.current = onHoverIndex;
    onRangeChangeRef.current = onRangeChange;
    rangeRef.current = range;
  }, [onHoverIndex, onRangeChange, range]);

  // Zones are drawn on the canvas, so a change to them is a redraw rather than a
  // rebuild: rebuilding would drop the zoom and flicker the whole stack.
  useEffect(() => {
    zonesRef.current = zones;
    plotRef.current?.redraw();
  }, [zones]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || spec.series.length === 0) return;

    const height = spec.height ?? 150;
    const data = [distance, ...spec.series.map((series) => series.values)];
    // Assigned once the plot exists, since the labels hang off its plotting area,
    // but referenced from a hook declared before it.
    let labels: CursorLabels | null = null;

    const options: uPlot.Options = {
      width: container.clientWidth,
      height,
      legend: { show: false },
      cursor: {
        sync: { key: SYNC_KEY, scales: ["x", null] },
        focus: { prox: 24 },
        points: { size: 6 },
        // The vertical line is the one that means something across a stack. A
        // horizontal one would only add a line per chart that lands wherever the
        // pointer happens to be.
        y: false,
      },
      scales: {
        x: { time: false },
        ...(spec.range ? { y: { range: spec.range } } : {}),
      },
      axes: [
        {
          // Drawn on every chart, labelled only under the last: the vertical grid
          // is what lets a braking point be read across the whole stack, and a
          // chart without it is a shape floating on its own.
          size: showXAxis ? X_AXIS_SIZE : X_AXIS_SIZE_HIDDEN,
          label: showXAxis ? axisLabel : undefined,
          values: showXAxis ? undefined : (_self, splits) => splits.map(() => ""),
          stroke: AXIS_STROKE,
          grid: { stroke: GRID_STROKE },
          ticks: { stroke: GRID_STROKE, size: showXAxis ? 4 : 0 },
        },
        {
          size: Y_AXIS_SIZE,
          stroke: AXIS_STROKE,
          grid: { stroke: GRID_STROKE },
          ticks: { stroke: GRID_STROKE },
          // Half a gear is not a gear.
          ...(spec.stepped ? { incrs: [1, 2, 5] } : {}),
        },
      ],
      series: [
        { label: "Distance" },
        ...spec.series.map((series) => ({
          label: series.label,
          stroke: series.colour,
          width: 1.5,
          spanGaps: true,
          ...(spec.stepped ? { paths: STEPPED_PATH } : {}),
        })),
      ],
      hooks: {
        // Behind the traces, not over them: the shading marks where to look, and a
        // wash over the lines would make the thing it points at harder to read.
        drawClear: [(self) => drawZones(self, zonesRef.current, badgeZones)],
        setCursor: [
          (self) => {
            labels?.update(self);
            onHoverRef.current(self.cursor.idx ?? null);
          },
        ],
        setSize: [(self) => labels?.update(self)],
        setScale: [
          (self, key) => {
            labels?.update(self);
            if (key !== "x") return;
            const report = onRangeChangeRef.current;
            if (!report) return;

            const { min, max } = self.scales.x;
            if (min === undefined || max === undefined) return;

            const full = fullRange(distance);
            const next =
              full && closeTo(min, full.min) && closeTo(max, full.max)
                ? null
                : { min, max };

            // Applying a range programmatically fires this hook too, so only report a
            // change the parent did not already ask for. Otherwise the two updates
            // chase each other.
            if (sameRange(next, rangeRef.current)) return;
            report(next);
          },
        ],
        draw: spec.zeroLine
          ? [
              (self) => {
                const y = self.valToPos(0, "y", true);
                const ctx = self.ctx;
                ctx.save();
                ctx.strokeStyle = AXIS_STROKE;
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.moveTo(self.bbox.left, y);
                ctx.lineTo(self.bbox.left + self.bbox.width, y);
                ctx.stroke();
                ctx.restore();
              },
            ]
          : [],
      },
    };

    const plot = new uPlot(options, data as unknown as uPlot.AlignedData, container);
    plotRef.current = plot;
    labels = new CursorLabels(plot, spec);

    // Adding a channel rebuilds the plot, which starts at the full lap. Reapply the
    // zoom here rather than leaving it to the effect below, whose dependencies have
    // not changed and so will not fire.
    if (rangeRef.current) {
      plot.setScale("x", rangeRef.current);
    }

    const observer = new ResizeObserver(() => {
      plot.setSize({ width: container.clientWidth, height });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      plot.destroy();
      labels = null;
      plotRef.current = null;
    };
  }, [distance, spec, showXAxis, badgeZones, axisLabel]);

  // Kept out of the effect above so a zoom does not rebuild the plot.
  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;

    const target = range ?? fullRange(distance);
    if (!target) return;
    const { min, max } = plot.scales.x;
    if (min !== undefined && closeTo(min, target.min) && max !== undefined && closeTo(max, target.max)) {
      return;
    }
    plot.setScale("x", target);
  }, [range, distance]);

  if (spec.series.length === 0) return null;

  return (
    <div className="relative py-1">
      <div
        className="pointer-events-none absolute top-2 z-10 text-xs font-medium text-zinc-300"
        style={{ left: Y_AXIS_SIZE + 8 }}
      >
        {spec.label}
        {spec.unit && <span className="ml-1 text-zinc-500">({spec.unit})</span>}
      </div>
      <div ref={containerRef} className="w-full" />
    </div>
  );
}

const ZONE_FILL = "rgba(250, 204, 21, 0.07)";
const ZONE_FILL_ACTIVE = "rgba(250, 204, 21, 0.16)";
const ZONE_EDGE = "rgba(250, 204, 21, 0.45)";

/**
 * Shades the coached stretches of track across the plotting area.
 *
 * Amber rather than a per-zone colour: the lap colours are already spoken for, and a
 * band that competes with the traces for attention defeats the purpose of pointing
 * at them.
 */
function drawZones(plot: uPlot, zones: Zone[] | undefined, badges: boolean) {
  if (!zones || zones.length === 0) return;

  const ctx = plot.ctx;
  const { left, top, width, height } = plot.bbox;

  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, width, height);
  ctx.clip();

  for (const zone of zones) {
    const from = plot.valToPos(zone.fromM, "x", true);
    const to = plot.valToPos(zone.toM, "x", true);
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
    const start = Math.min(from, to);
    const span = Math.max(1, Math.abs(to - from));

    ctx.fillStyle = zone.active ? ZONE_FILL_ACTIVE : ZONE_FILL;
    ctx.fillRect(start, top, span, height);

    ctx.strokeStyle = ZONE_EDGE;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(start, top);
    ctx.lineTo(start, top + height);
    ctx.moveTo(start + span, top);
    ctx.lineTo(start + span, top + height);
    ctx.stroke();
    ctx.setLineDash([]);

    if (badges) {
      ctx.fillStyle = ZONE_EDGE;
      ctx.font = "600 10px ui-sans-serif, system-ui, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(zone.badge, start + 4, top + 3);
    }
  }

  ctx.restore();
}

/**
 * The value of every series at the cursor, printed against the right edge of its own
 * chart at the height of its own line.
 *
 * Reading a trace otherwise means estimating a value off a y axis five charts up,
 * for each lap on screen. The table under the stack gives the exact numbers; these
 * put an approximate one where the eye already is.
 *
 * Written straight into the DOM rather than rendered: this updates on every mouse
 * move, across every chart in the stack at once, and going through React for it
 * makes the cursor lag behind the pointer.
 */
class CursorLabels {
  private readonly pills: HTMLDivElement[];

  constructor(
    plot: uPlot,
    private readonly spec: ChartSpec,
  ) {
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:absolute; inset:0; overflow:hidden; pointer-events:none;";

    this.pills = spec.series.map((series) => {
      const pill = document.createElement("div");
      pill.style.cssText =
        "position:absolute; right:3px; display:none; padding:0 4px; border-radius:4px;" +
        "font-size:11px; line-height:16px; font-variant-numeric:tabular-nums;" +
        "background:rgba(9,9,11,0.85); white-space:nowrap;";
      pill.style.color = series.colour;
      pill.style.boxShadow = `inset 0 0 0 1px ${series.colour}55`;
      overlay.appendChild(pill);
      return pill;
    });

    plot.over.appendChild(overlay);
  }

  update(plot?: uPlot) {
    if (!plot) return;

    const index = plot.cursor.idx;
    const height = plot.over.clientHeight;
    // A child of the plotting area, whereas uPlot measures from the canvas.
    const top = plot.over.offsetTop;

    const placed: Array<{ pill: HTMLDivElement; y: number }> = [];

    this.spec.series.forEach((series, position) => {
      const pill = this.pills[position];
      const value = index === null || index === undefined ? null : series.values[index];

      if (value === null || value === undefined || !Number.isFinite(value)) {
        pill.style.display = "none";
        return;
      }

      pill.textContent = this.spec.format
        ? this.spec.format(value)
        : formatLoosely(value);
      pill.style.display = "block";
      placed.push({ pill, y: plot.valToPos(value, "y") - top });
    });

    // Two laps within a few km/h of each other would otherwise print one label on
    // top of the other, which is exactly where the difference matters.
    placed.sort((a, b) => a.y - b.y);
    let previous = -Infinity;
    for (const { pill, y } of placed) {
      const clamped = Math.min(
        Math.max(y, LABEL_SPACING_PX / 2, previous + LABEL_SPACING_PX),
        height - LABEL_SPACING_PX / 2,
      );
      previous = clamped;
      pill.style.top = `${clamped}px`;
      pill.style.transform = "translateY(-50%)";
    }
  }
}

/** Enough digits to be useful, few enough to fit, for a value of unknown scale. */
function formatLoosely(value: number): string {
  const magnitude = Math.abs(value);
  if (magnitude >= 100) return value.toFixed(0);
  if (magnitude >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function fullRange(distance: Float64Array): Range | null {
  if (distance.length === 0) return null;
  return { min: distance[0], max: distance[distance.length - 1] };
}

/** Half a metre: finer than the grid, so never a false difference. */
function closeTo(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.5;
}

function sameRange(a: Range | null, b: Range | null): boolean {
  if (a === null || b === null) return a === b;
  return closeTo(a.min, b.min) && closeTo(a.max, b.max);
}
